import GoogleMapsUtil from '@frontend/utils/GoogleMapsUtil';
import distance from '@turf/distance';
import getComponentInstanceFor from '@swey/vue-hydration/getComponentInstanceFor';
import { ShowMoreListPublicMethods } from '@components/011-ShowMoreList/ShowMoreList.client';
import { AnchorNavigationEvents } from '@frontend/utils/AnchorNavigationUtil';
import BaseSlider from '@components/base/BaseSlider';
import * as Sentry from '@sentry/browser';
import { pushToDataLayer } from '@frontend/utils/TrackingUtil';
import UserConsentUtil from '@frontend/utils/UserConsentUtil';

import AutocompleteOptions = google.maps.places.AutocompleteOptions;

export default class PlacesGeoSortList {
	private readonly element: HTMLElement;

	public static domSelectors = {
		input: '.c-places-geo-sort-list__search-input',
		sortableItem: '[data-geo-sort-item]',
		resultWrapper: '.c-show-more-list__list',
		showMoreList: '.c-show-more-list',
		closeButton: '.c-places-geo-sort-list__close-button',
		geoButton: '.c-places-geo-sort-list__detect-button'
	};

	private autocomplete?: google.maps.places.Autocomplete;

	private filterableElements: HTMLElement[] = [];

	protected options: Record<string, any> = {};

	public constructor(element: HTMLElement) {
		this.element = element;
		const me = this;
		window.addEventListener('CookiebotOnConsentReady', function initMyself(): void {
			me.init();
		});
	}

	private async init(): Promise<void> {
		if (!UserConsentUtil.marketingConsent) {
			const geolistelement = document.getElementsByClassName('c-places-geo-sort-list__search');
			const warningtext = geolistelement[0].getAttribute('data-warning');
			const warning = document.createElement('div');
			warning.classList.add('m-maps__warning');
			warning.classList.add('fs-copy-a3');
			warning.innerHTML = warningtext || ' ';
			geolistelement[0].addEventListener('click', function showCookiebotAgain(): boolean {
				const mapelement = document.getElementsByClassName('c-places-geo-sort-list__search');
				mapelement[0].appendChild(warning);
				UserConsentUtil.requestConsent();
				window.addEventListener(
					'CookiebotOnAccept',
					function reloadPageAfterCookiebot(): void {
						if (UserConsentUtil.marketingConsent) {
							window.location.reload();
						}
					},
					false
				);
				return false;
			});
			return;
		}
		const optionsString = this.element.getAttribute('data-options');
		this.options = optionsString ? JSON.parse(optionsString) : {};

		await GoogleMapsUtil.initApi();

		const input = this.element.querySelector(PlacesGeoSortList.domSelectors.input) as HTMLInputElement;
		const resetButton = this.element.querySelector(PlacesGeoSortList.domSelectors.closeButton) as HTMLElement;
		const geoButton = this.element.querySelector(PlacesGeoSortList.domSelectors.geoButton) as HTMLElement;

		const countryCode = window.HARIBO.global.locale.split('-')[1] || 'de';
		const options: AutocompleteOptions = {
			componentRestrictions: { country: countryCode },
			fields: ['geometry'],
			types: ['locality', 'postal_code']
		};

		this.autocomplete = new google.maps.places.Autocomplete(input, options);

		this.autocomplete.addListener('place_changed', (): void => {
			if (!this.autocomplete) return;

			const place = this.autocomplete.getPlace();

			if (place.geometry) {
				this.sortAndPrepend([Number(place.geometry.location.lat()), Number(place.geometry.location.lng())]);
			}
		});

		input.addEventListener('input', (): void => {
			if (!input.value) {
				this.resetList();
			}
		});

		const push = (query: string): void => {
			pushToDataLayer({
				event: 'ux-shop-search',
				query
			});
		};

		input.addEventListener('change', (): void => {
			if (input.value) {
				setTimeout((): void => {
					const d = document.querySelector(PlacesGeoSortList.domSelectors.input) as HTMLInputElement;
					push(d.value);
				}, 500);
			}
		});

		input.addEventListener('blur', (): void => {
			if (!input.value) {
				this.resetList();
			}
		});

		resetButton.addEventListener('click', (): void => {
			input.value = '';
			this.resetList();
		});

		if (navigator.geolocation) {
			this.element.classList.add('has-geo-support');
			geoButton.addEventListener('click', this.detectLocation.bind(this));
		}

		document.addEventListener(AnchorNavigationEvents.change, this.anchorLinkHandler.bind(this));

		// used for isolated styling of predefined 3rd party CSS (see CSS file of component)
		document.body.classList.add('has-places-autocomplete-field');

		this.setSnapshotOfFilterableDomElements();
	}

	private setSnapshotOfFilterableDomElements(): void {
		this.filterableElements = Array.from(
			this.element.querySelectorAll(PlacesGeoSortList.domSelectors.sortableItem)
		) as HTMLElement[];
	}

	/**
	 * Detects the current location of the user and sorts the list by distance
	 * Permission is asked by the browser, if not already granted
	 * @private
	 */
	private detectLocation(): void {
		const GeoCoder = new google.maps.Geocoder();
		const input = this.element.querySelector(PlacesGeoSortList.domSelectors.input) as HTMLInputElement;

		// get current lat long from device
		navigator.geolocation.getCurrentPosition(
			(position): void => {
				// use Google Maps geocoder to get city and country from lat long
				GeoCoder.geocode(
					{ location: { lat: position.coords.latitude, lng: position.coords.longitude } },
					(results, status): void => {
						if (status === 'OK') {
							if (results[0]) {
								// get address components for city and country
								const localityObject = results[0].address_components.find((component): boolean =>
									component.types.includes('locality')
								);

								const countryObject = results[0].address_components.find((component): boolean =>
									component.types.includes('country')
								);

								const locality = localityObject ? localityObject.long_name : '';
								const country = countryObject ? countryObject.long_name : '';

								// set value of input field to city and country
								input.value = `${locality}, ${country}`;

								// sort list by distance
								this.sortAndPrepend([position.coords.latitude, position.coords.longitude]);
							}
						}
					}
				);
			},
			(error): void => {
				console.error(error);
				Sentry.captureException(error);
			}
		);
	}

	private sortAndPrepend(latLng: [number, number]): void {
		const radiusKm = this.options.radiusKm || 100;

		const sortedElements = this.filterableElements
			.filter((element: HTMLElement): boolean => !!element.getAttribute('data-lat-lng'))
			.map(
				(element: HTMLElement): IElementWithDistance => {
					const elementLatLng = PlacesGeoSortList.getLatLngFromElement(element) as [number, number];
					const distanceTo = distance(latLng, elementLatLng, { units: 'kilometers' });
					return { element, distanceTo };
				}
			)
			.filter((element): boolean => element.distanceTo < radiusKm)
			.sort((a, b): number => a.distanceTo - b.distanceTo)
			.map((element): HTMLElement => element.element);

		this.element.setAttribute('data-total-sort', sortedElements.length.toString());
		this.prependElements(sortedElements);
	}

	private emptyContainer(): void {
		const elements = Array.from(
			this.element.querySelectorAll(PlacesGeoSortList.domSelectors.sortableItem)
		) as HTMLElement[];

		elements.forEach((element: HTMLElement): void => element.remove());
	}

	private prependElements(elements: HTMLElement[]): void {
		const resultContainer = this.element.querySelector(PlacesGeoSortList.domSelectors.resultWrapper);

		if (resultContainer) {
			this.emptyContainer();

			const fragment = new DocumentFragment();

			elements.forEach((element): void => fragment.append(element));
			resultContainer.prepend(fragment);

			setTimeout((): void => {
				BaseSlider.resizeSliders(this.element);
			}, 10);

			this.updateShowMoreList();
		}
	}

	private resetList(): void {
		this.element.removeAttribute('data-total-sort');
		this.prependElements(this.filterableElements);
	}

	private updateShowMoreList(): void {
		const showMoreList = this.element.querySelector(PlacesGeoSortList.domSelectors.showMoreList) as HTMLElement;

		if (!showMoreList) {
			return;
		}

		const instance = getComponentInstanceFor(showMoreList) as ShowMoreListPublicMethods;

		if (instance) {
			instance.resetAndUpdate();
		}
	}

	private static getLatLngFromElement(element: HTMLElement): [number, number] | null {
		const latLngString = element.getAttribute('data-lat-lng');

		if (latLngString) {
			return [Number(latLngString.split(',')[0]), Number(latLngString.split(',')[1])];
		}

		return null;
	}

	private anchorLinkHandler(event: CustomEventInit): void {
		const targetElement = this.element.querySelector(event.detail.target);
		const useGeoSort = targetElement ? targetElement.hasAttribute('data-geo-sort-item') : false;
		const input = this.element.querySelector(PlacesGeoSortList.domSelectors.input) as HTMLInputElement;

		if (useGeoSort && targetElement) {
			const elementLatLng = PlacesGeoSortList.getLatLngFromElement(targetElement as HTMLElement);
			const location = targetElement.getAttribute('data-location');
			if (elementLatLng && location) {
				this.sortAndPrepend(elementLatLng);
				input.value = location;
			}
		}
	}
}

interface IElementWithDistance {
	element: HTMLElement;
	distanceTo: number;
}
